import { getTranslation } from '@/lib/utils/i18n';
import { ESCA_BONUS_CAP } from './declarationOptions';

// Option lists shared by the entity forms; per-slice variation is passed in, never forked.

// The value carries which kind it is; `storage` is the top bar's switcher only.
export const FACILITY_KINDS = Object.freeze({
  site: 'site',
  storage: 'storage',
  backToBack: 'b2b',
});

const FACILITY_SEPARATOR = ':';

export const buildFacilityValue = (kind, id) => `${kind}${FACILITY_SEPARATOR}${id}`;

export const parseFacilityValue = (value) => {
  const separator = (value ?? '').indexOf(FACILITY_SEPARATOR);
  if (separator === -1) return { kind: null, id: null };
  return { kind: value.slice(0, separator), id: value.slice(separator + 1) };
};

// The server's `material_type` value for a material that carries no certification.
const CONVENTIONAL_MATERIAL = 'CONVENTIONAL';

// Entity forms take certified materials only — an order with a conventional one is rejected outright.
export const isCertifiedMaterial = (material) => material.materialType !== CONVENTIONAL_MATERIAL;

// A conventional material can't go on a contract, shipment or order, so it stocks nothing usable.
const stocksMaterial = (siteId, materials) =>
  materials.some(
    (material) => isCertifiedMaterial(material) && material.siteIds?.includes(String(siteId)),
  );

/**
 * Sites then back-to-back accounts, as one "Received at"/"Facility" list.
 *
 * @param materials        when given, a site stocking none is disabled and sinks to the bottom
 * @param noMaterialsLabel sub-label for such a site
 */
export const buildFacilityOptions = ({
  sites = [],
  backToBackAccounts = [],
  materials = null,
  noMaterialsLabel,
} = {}) => {
  const siteOptions = sites.map((site) => {
    const option = {
      value: buildFacilityValue(FACILITY_KINDS.site, site.id),
      label: site.name,
      badgeText: site.code || undefined,
    };
    if (materials != null && !stocksMaterial(site.id, materials)) {
      return { ...option, disabled: true, subLabel: noMaterialsLabel };
    }
    return option;
  });
  // A site with nothing to pick sinks to the bottom rather than blocking the ones that do.
  const sortedSiteOptions = [...siteOptions].sort(
    (a, b) => Number(Boolean(a.disabled)) - Number(Boolean(b.disabled)),
  );
  return [
    ...sortedSiteOptions,
    ...backToBackAccounts.map((account) => ({
      value: buildFacilityValue(FACILITY_KINDS.backToBack, account.id),
      label: account.name,
      badgeText: account.code || undefined,
    })),
  ];
};

// Any lookup list ({ id, name, code? }) → dropdown options; `code` becomes the badge.
export const buildLookupOptions = (items = []) =>
  items.map((item) => ({ value: item.id, label: item.name, badgeText: item.code || undefined }));

// Only sites have terminals.
export const buildTerminalOptions = (site) =>
  (site?.terminals ?? []).map((terminal) => ({
    value: terminal.id,
    label: terminal.name,
    badgeText: terminal.code || undefined,
  }));

/**
 * Certified materials the chosen site stocks; a null siteId (back-to-back) skips the site filter.
 *
 * @param certificateType      when given, also scopes to the active module's materials
 * @param terminalMaterialIds  the ids assigned to the picked terminal; null when none is picked
 */
export const buildMaterialOptions = ({
  materials = [],
  siteId = null,
  certificateType,
  terminalMaterialIds = null,
} = {}) =>
  materials
    .filter(isCertifiedMaterial)
    .filter((material) => siteId == null || material.siteIds?.includes(String(siteId)))
    .filter((material) => terminalMaterialIds == null || terminalMaterialIds.has(material.id))
    .filter(
      (material) =>
        !certificateType ||
        !material.certificateType ||
        material.certificateType === certificateType,
    )
    .map((material) => ({ value: material.id, label: material.name }));

// The server's `feedstock_category` enum verbatim.
export const FEEDSTOCK_CATEGORY = Object.freeze({
  annexIxA: 'ANNEX_IX_A',
  annexIxB: 'ANNEX_IX_B',
  crop: 'CROP',
});

const FEEDSTOCK_LABEL_KEYS = Object.freeze({
  [FEEDSTOCK_CATEGORY.annexIxA]: 'FEEDSTOCK_ANNEX_IX_A',
  [FEEDSTOCK_CATEGORY.annexIxB]: 'FEEDSTOCK_ANNEX_IX_B',
  [FEEDSTOCK_CATEGORY.crop]: 'FEEDSTOCK_CROP',
});

export const buildFeedstockCategoryOptions = (t) =>
  Object.values(FEEDSTOCK_CATEGORY).map((value) => ({
    value,
    label: t(FEEDSTOCK_LABEL_KEYS[value]),
  }));

// For non-React callers (the mappers).
export const feedstockCategoryLabel = (value) =>
  value ? getTranslation(FEEDSTOCK_LABEL_KEYS[value] ?? '') || value : '';

const ESCA_BONUS_CAP_LABEL_KEYS = Object.freeze({
  [ESCA_BONUS_CAP.notClaimed]: 'ESCA_BONUS_CAP_NOT_CLAIMED',
  [ESCA_BONUS_CAP.manureBonus]: 'ESCA_BONUS_CAP_MANURE_BONUS',
  [ESCA_BONUS_CAP.noCapBefore2013]: 'ESCA_BONUS_CAP_NO_CAP_BEFORE_2013',
  [ESCA_BONUS_CAP.biofuelAfter2023]: 'ESCA_BONUS_CAP_BIOFUEL_AFTER_2023',
  [ESCA_BONUS_CAP.biocharUse]: 'ESCA_BONUS_CAP_BIOCHAR_USE',
  [ESCA_BONUS_CAP.priorEscaClaims]: 'ESCA_BONUS_CAP_PRIOR_ESCA_CLAIMS',
});

// For non-React callers (the mappers). Unknown/garbage falls back to the raw value.
export const escaBonusCapLabel = (value) =>
  value ? getTranslation(ESCA_BONUS_CAP_LABEL_KEYS[value] ?? '') || value : '';

// The server's `preferred_allocation` enum verbatim.
export const ALLOCATION_PREFERENCE = Object.freeze({
  france: 'FRANCE',
  belgium: 'BELGIUM',
  denmark: 'DENMARK',
  estonia: 'ESTONIA',
  netherlands: 'NETHERLANDS',
  italy: 'ITALY',
  portugal: 'PORTUGAL',
  singleCounting: 'SINGLE_COUNTING',
  advanced: 'ADVANCED',
});

const allocationLabelKey = (value) => `ALLOCATION_${value}`;

// At most three countries may be allocated against, on a contract or a shipment.
export const MAX_ALLOCATION_PREFERENCES = 3;

// Neither is a country, so neither combines with one — or with the other.
const EXCLUSIVE_ALLOCATION_PREFERENCES = Object.freeze([
  ALLOCATION_PREFERENCE.singleCounting,
  ALLOCATION_PREFERENCE.advanced,
]);

const isExclusiveAllocation = (value) => EXCLUSIVE_ALLOCATION_PREFERENCES.includes(value);

/**
 * The form's option list: the two either/or rows marked, and the countries greyed out
 * once the cap is reached. An exclusive row is never capped — picking it clears the rest.
 */
export const buildAllocationFieldOptions = (options, selected = []) => {
  const capped = selected.filter((value) => !isExclusiveAllocation(value)).length;
  return options.map((option) => {
    const exclusive = isExclusiveAllocation(option.value);
    if (exclusive) return { ...option, exclusive: true };
    if (capped < MAX_ALLOCATION_PREFERENCES || selected.includes(option.value)) return option;
    return { ...option, disabled: true };
  });
};

/** @param includeSingleCounting contracts let Multi site transfer own that flag instead */
export const buildAllocationPreferenceOptions = (t, { includeSingleCounting = true } = {}) =>
  Object.values(ALLOCATION_PREFERENCE)
    .filter((value) => includeSingleCounting || value !== ALLOCATION_PREFERENCE.singleCounting)
    .map((value) => ({ value, label: t(allocationLabelKey(value)) }));

export const allocationPreferenceLabel = (value) => getTranslation(allocationLabelKey(value));

// A contract/batch carries preferredAllocation as a list; an order/shipment/fulfillment carries a single scalar. Normalizes either into an array so a mapper can always .map() it.
export const toAllocationPreferenceArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

// Yes/No options over a slice's own value pair — the two endpoints accept different shapes.
export const createYesNoOptions =
  ({ yes, no }) =>
  (t) => [
    { value: no, label: t('COMMON_NO') },
    { value: yes, label: t('COMMON_YES') },
  ];

export const CHAIN_OF_CUSTODY_OPTION = Object.freeze({
  massBalance: 'MASS_BALANCE',
  physicalSegregation: 'PHYSICAL_SEGREGATION',
});

// A declaration's chain-of-custody option — shared by every declaration-editing flow.
export const buildChainOfCustodyOptions = (t) => [
  { value: CHAIN_OF_CUSTODY_OPTION.massBalance, label: t('CHAIN_OF_CUSTODY_MASS_BALANCE') },
  {
    value: CHAIN_OF_CUSTODY_OPTION.physicalSegregation,
    label: t('CHAIN_OF_CUSTODY_PHYSICAL_SEGREGATION'),
  },
];
